import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LanguageProvider } from "@/i18n/LanguageContext";

const PrivacyContent = () => (
  <div className="min-h-screen bg-background">
    <Navbar />
    <div className="container mx-auto px-4 py-24 max-w-3xl">
      <h1 className="font-display text-3xl md:text-4xl text-foreground mb-8">Privacy Policy</h1>
      <p className="text-muted-foreground mb-8 text-sm">Last updated: March 2026</p>

      <div className="prose prose-lg max-w-none text-foreground/80">
        <p>
          Vita Dom ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your information when you use our
          website and services.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">1. Data Collection</h2>
        <p>We collect information that you provide directly to us, including:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Personal identification information (name, email address, phone number)</li>
          <li>Billing and payment information (processed securely via Stripe)</li>
          <li>Information about your care needs and preferences</li>
          <li>Communications you send to us (emails, messages, support requests)</li>
        </ul>
        <p className="mt-4">We also automatically collect certain information when you visit our website:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Device and browser information</li>
          <li>IP address and approximate location</li>
          <li>Pages visited and interactions with our site</li>
          <li>Referring website or source</li>
        </ul>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">2. Data Usage</h2>
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Provide, maintain, and improve our care services</li>
          <li>Process bookings and payments</li>
          <li>Match families with appropriate caregivers</li>
          <li>Communicate with you about your account, bookings, and updates</li>
          <li>Analyze usage patterns to improve our website and services</li>
          <li>Comply with legal obligations</li>
        </ul>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">3. Cookies</h2>
        <p>
          Our website uses cookies and similar tracking technologies to enhance your experience.
          These include:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Essential cookies:</strong> Required for the website to function properly (session management, language preferences)</li>
          <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our website (via PostHog)</li>
        </ul>
        <p className="mt-4">
          You can control cookies through your browser settings. Disabling certain cookies may
          affect website functionality.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">4. Third-Party Services</h2>
        <p>We share data with the following third-party services, each with their own privacy policies:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Stripe:</strong> Payment processing. Stripe handles all payment data securely and is PCI-DSS compliant. <a href="https://stripe.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
          <li><strong>Supabase:</strong> Database and authentication services. Data is stored securely in EU-based infrastructure. <a href="https://supabase.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Supabase Privacy Policy</a></li>
          <li><strong>PostHog:</strong> Product analytics to understand usage patterns and improve our service. <a href="https://posthog.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">PostHog Privacy Policy</a></li>
        </ul>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">5. Your Rights (GDPR)</h2>
        <p>Under the General Data Protection Regulation (GDPR), you have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
          <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data</li>
          <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
          <li><strong>Restriction:</strong> Request limitation of processing of your data</li>
          <li><strong>Portability:</strong> Request transfer of your data in a machine-readable format</li>
          <li><strong>Objection:</strong> Object to processing of your data for certain purposes</li>
          <li><strong>Withdraw consent:</strong> Withdraw consent at any time where processing is based on consent</li>
        </ul>
        <p className="mt-4">
          To exercise any of these rights, please contact us at the address below. We will respond
          to your request within 30 days.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">6. Data Retention</h2>
        <p>
          We retain your personal data only for as long as necessary to fulfill the purposes for
          which it was collected, including to satisfy legal, accounting, or reporting requirements.
          When data is no longer needed, it is securely deleted or anonymized.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">7. Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect your personal
          data against unauthorized access, alteration, disclosure, or destruction. However, no
          method of transmission over the Internet is 100% secure.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">8. Contact</h2>
        <p>
          If you have questions about this Privacy Policy or wish to exercise your data rights,
          please contact us:
        </p>
        <ul className="list-none pl-0 space-y-1 mt-4">
          <li><strong>Vita Dom</strong></li>
          <li>Email: <a href="mailto:office@vita-dom.org" className="text-primary hover:underline">office@vita-dom.org</a></li>
        </ul>
      </div>
    </div>
    <Footer />
  </div>
);

export default function Privacy() {
  return (
    <LanguageProvider>
      <PrivacyContent />
    </LanguageProvider>
  );
}
